import React from "react";
import { Order } from "../components/EnhancedTableHead/EnhancedTableHeadInterfaces";

const defaultRowsPerPage = 25;
interface IUseTableOperations {
    getComparator<Key extends string | number | symbol>(
        order: Order,
        orderBy: Key,
    ): (a: { [key in Key]: string | number }, b: { [key in Key]: string | number }) => number;
    handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
    stableSort<T>(array: Object[], comparator: (a: T, b: T) => number): T[];
    handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleChangePage: (event: unknown, newPage: number) => void;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage: number;
}

export const useTableOperations = (): IUseTableOperations => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState<string>("");

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
        const aLowerCased =
            typeof a[orderBy] === "string" || !a[orderBy] ? String(a[orderBy]).toLowerCase() : a[orderBy];
        const bLowerCased =
            typeof b[orderBy] === "string" || !b[orderBy] ? String(b[orderBy]).toLowerCase() : b[orderBy];
        if (bLowerCased < aLowerCased) {
            return -1;
        }
        if (bLowerCased > aLowerCased) {
            return 1;
        }
        return 0;
    }

    function getComparator<Key extends keyof any>(
        order: Order,
        orderBy: Key,
    ): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
        return order === "desc"
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    function stableSort<T>(array: Object[], comparator: (a: T, b: T) => number) {
        const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) {
                return order;
            }
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    return {
        getComparator,
        handleRequestSort,
        handleChangeRowsPerPage,
        handleChangePage,
        order,
        orderBy,
        page,
        rowsPerPage,
        stableSort,
    };
};
